import { Select } from 'antd';
import debounce from 'lodash/debounce';
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';

import type { MultipleSelectType } from '@app/components/MultipleSelect/MultipleSelect.types';
import {
  optionRender,
  popupRender,
} from '@app/components/MultipleSelect/utils/';
import { FRONT_WARNING } from '@app/constants';
import type { NLeavePageProvider } from '@app/providers/LeavePageProvider/LeavePageProvider.types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { generateSpanWithDataTestId } from '@app/shared/utils';

import type { BaseOptionType } from 'antd/es/select';

import '@app/components/MultipleSelect/styles/MultipleSelect.desktop.scss';

const ITEMS_PER_PAGE = 50;
const SEARCH_CACHE_SIZE = 10;
// Поиск по опциям идёт от трёх символов: по одному-двум символам фильтр
// перебирает почти весь список и мешает набирать запрос дальше (issue/11072).
const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;

export const MultipleSelect: MultipleSelectType = ({
  allOptions = [],
  allowClear = false,
  showActions = true,
  width = 180,
  minWidth = 180,
  selectedOption,
  placeholder = 'Все варианты',
  setSelectedOptions,
  className,
  loader,
  size,
  filterName = 'label',
  disabled = false,
  setNewData,
  isGroupOption = false,
  dataTestId,
  dropdownDataTestId,
  showSearch = true,
  placeHolderDataTestId = '',
  optionDataTestId = '',
  isListLazyLoading = false,
}) => {
  const [isOpened, setIsOpened] = useState(false);
  // `searchValue` — то, что видно в поле ввода: обновляется на каждый символ,
  // иначе управляемый Select возвращает в поле прежнее значение и набранные
  // символы пропадают. `appliedSearchValue` — запрос, по которому фильтруются
  // опции: он отстаёт на debounce и включается от MIN_SEARCH_LENGTH символов.
  const [searchValue, setSearchValue] = useState('');
  const [appliedSearchValue, setAppliedSearchValue] = useState('');
  const { showNotification } = useNotification();

  const [visibleItems, setVisibleItems] = useState(ITEMS_PER_PAGE);
  const searchCache = useRef(new Map<string, BaseOptionType[]>());
  const observer = useRef<IntersectionObserver | null>(null);

  const applySearch = useMemo(
    () =>
      debounce((value: string) => {
        setAppliedSearchValue(value.length >= MIN_SEARCH_LENGTH ? value : '');
        setVisibleItems(ITEMS_PER_PAGE);
      }, SEARCH_DEBOUNCE_MS),
    []
  );

  useEffect(() => () => applySearch.cancel(), [applySearch]);

  const resetSearch = useCallback(() => {
    applySearch.cancel();
    setSearchValue('');
    setAppliedSearchValue('');
    setVisibleItems(ITEMS_PER_PAGE);
  }, [applySearch]);

  const unselectAll = useCallback(() => {
    setSelectedOptions([]);
    resetSearch();
  }, [resetSearch, setSelectedOptions]);

  const handleChange = useCallback(
    (value: BaseOptionType[], option: BaseOptionType | undefined) => {
      if (!option)
        return showNotification(
          'Информация по этому переключателю не найдена',
          FRONT_WARNING
        );

      setNewData &&
        setNewData((prev: NLeavePageProvider.Data) => {
          return {
            ...prev,
            tags: value,
          };
        });

      return setSelectedOptions(option as BaseOptionType[]);
    },
    [setNewData, setSelectedOptions, showNotification]
  );

  const searchHandler = useCallback(
    (value: string) => {
      setSearchValue(value);
      applySearch(value);
    },
    [applySearch]
  );

  const groupOptions = useCallback(
    (options: BaseOptionType[]) => {
      if (!isListLazyLoading) return options;

      const groups = new Map<string, BaseOptionType[]>();

      options.forEach((option) => {
        const label = String(option[filterName]);
        const firstLetter = label.charAt(0).toUpperCase();

        const hasGroup = groups.has(firstLetter);

        if (!hasGroup) {
          groups.set(firstLetter, []);
        }

        groups.get(firstLetter)?.push(option);
      });

      return Array.from(groups.entries()).map(([letter, items]) => ({
        label: letter,
        options: items,
      }));
    },
    [filterName, isListLazyLoading]
  );

  const filteredOptions = useMemo(() => {
    if (!appliedSearchValue) {
      return groupOptions(allOptions);
    }

    const hasCachedValue = searchCache.current.has(appliedSearchValue);

    if (hasCachedValue) {
      return groupOptions(
        searchCache.current.get(appliedSearchValue)!.slice(0, visibleItems)
      );
    }

    const searchLower = appliedSearchValue.toLowerCase();

    let filtered;

    if (isGroupOption) {
      filtered = (
        allOptions as { label: React.ReactNode; options: BaseOptionType[] }[]
      )
        ?.map((group) => {
          const matchedOptions = group.options?.filter((option) =>
            String(option[filterName])?.toLowerCase().includes(searchLower)
          );

          if (matchedOptions?.length > 0) {
            return {
              label: group.label,
              options: matchedOptions,
            };
          }

          return null;
        })
        ?.filter(Boolean) as {
        label: React.ReactNode;
        options: BaseOptionType[];
      }[];
    } else {
      const filteredOptions = allOptions?.filter((option) => {
        return String(option[filterName])?.toLowerCase()?.includes(searchLower);
      });
      filtered = filteredOptions;
    }

    if (!isGroupOption) {
      if (searchCache.current.size >= SEARCH_CACHE_SIZE) {
        const firstKey = searchCache.current.keys().next().value;
        if (firstKey) {
          searchCache.current.delete(firstKey);
        }
      }

      searchCache.current.set(appliedSearchValue, filtered);
    }

    return groupOptions(
      isGroupOption
        ? filtered
        : (filtered as BaseOptionType[]).slice(0, visibleItems)
    );
  }, [
    appliedSearchValue,
    isGroupOption,
    groupOptions,
    visibleItems,
    allOptions,
    filterName,
  ]);

  const selectAll = useCallback(() => {
    let optionsToSelect: BaseOptionType[] = [];

    if (isGroupOption) {
      optionsToSelect = filteredOptions.flatMap((group) => group.options);
    } else {
      optionsToSelect = filteredOptions;
    }

    setSelectedOptions(optionsToSelect);
    resetSearch();
  }, [filteredOptions, isGroupOption, resetSearch, setSelectedOptions]);

  const loadMore = useCallback(() => {
    setVisibleItems((prev) => prev + ITEMS_PER_PAGE);
  }, []);

  const lastOptionRefCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (observer.current) {
        observer.current.disconnect();
      }

      if (node) {
        observer.current = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) {
            loadMore();
          }
        });

        observer.current.observe(node);
      }
    },
    [loadMore]
  );

  useEffect(() => {
    if (!isOpened && !selectedOption) {
      resetSearch();
    }
    if (isOpened && selectedOption?.length === 0) {
      resetSearch();
    }
  }, [isOpened, resetSearch, selectedOption]);

  const isEmptyOptions =
    Array.isArray(filteredOptions) && filteredOptions.length === 0;

  return (
    <Select
      options={filteredOptions}
      value={selectedOption}
      onChange={handleChange}
      onSearch={searchHandler}
      virtual
      optionLabelProp="label"
      maxTagCount="responsive"
      mode="multiple"
      open={isOpened}
      onOpenChange={setIsOpened}
      filterOption={false}
      popupMatchSelectWidth={true}
      allowClear={allowClear}
      className={className}
      disabled={disabled}
      size={size}
      loading={loader}
      showSearch={showSearch}
      searchValue={searchValue}
      style={{ width: width, minWidth: minWidth }}
      classNames={{ popup: { root: 'multiselect-popup' } }}
      placeholder={generateSpanWithDataTestId(
        placeholder,
        placeHolderDataTestId
      )}
      optionRender={optionRender({
        lastOptionRefCallback,
        optionDataTestId,
        length: filteredOptions.length,
      })}
      popupRender={popupRender({
        showActions,
        dropdownDataTestId,
        selectAll,
        unselectAll,
        isEmptyOptions,
      })}
      {...(dataTestId && { 'data-testid': dataTestId })}
    />
  );
};
